import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import ErrorHandler from "../../helpers/ErrorHandler";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { AxiosError, AxiosInstance } from "axios";
import { Activity, ActivityCategory, NotificationMessage } from "../../helpers/Types";

interface ActivityListProps {
  setNotificationMessage: (notificationMessage: NotificationMessage) => void;
  api: AxiosInstance;
}

export default function ActivityList({ setNotificationMessage, api }: ActivityListProps) {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [activityCategories, setActivityCategories] = useState([]);

  const getActivityCategories = useCallback(() => {
    api
      .get(`activity/get-categories`)
      .then((response) => {
        console.log(response.data.Items);
        if (response.data) setActivityCategories(response.data.Items);
      })
      .catch((error: AxiosError) => {
        ErrorHandler(error, setNotificationMessage);
        navigate("/login");
      });
  }, [api, setActivityCategories, setNotificationMessage, navigate]);

  useEffect(() => {
    getActivityCategories();
  }, [getActivityCategories]);

  const updateActivities = useCallback(
    (filter: string) => {
      let url = `activity/list/`;
      if (filter) url += encodeURIComponent(filter);
      api
        .get(url)
        .then((response) => {
          if (response.data) setActivities(response.data);
        })
        .catch((error: AxiosError) => {
          ErrorHandler(error, setNotificationMessage);
          navigate("/login");
        });
    },
    [api, setActivities, setNotificationMessage, navigate]
  );

  useEffect(() => {
    updateActivities("");
  }, [updateActivities]);

  const filterActivities = (filter: string) => {
    updateActivities(filter);
  };

  function confirmDelete(id: string) {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="text-center">
            <h2>Confirm Delete</h2>
            <p className="mb-4">Are you sure you want to delete this item?</p>
            <button className="btn btn-danger me-3" onClick={onClose}>
              No
            </button>
            <button
              className="btn btn-success"
              onClick={() => {
                deleteData(id);
                onClose();
              }}
            >
              Yes, Delete it!
            </button>
          </div>
        );
      },
    });
  }

  function deleteData(created_at: string) {
    api
      .delete(`activity/delete`, {
        data: { created_at: created_at },
      })
      .then(() => {
        setNotificationMessage({ class: "success", message: "Deleted successfully" });
        updateActivities("");
      })
      .catch((error) => {
        ErrorHandler(error, setNotificationMessage);
      });
  }

  const renderActivities = activities.map((activity: Activity, index: number) => {
    let date = new Date(parseInt(activity.created_at.S));
    return (
      <tr key={activity.created_at.S}>
        <td>{index + 1}</td>
        <td>{activity.activity.S}</td>
        <td>
          {date.toLocaleDateString("en-CA")}&nbsp;
          {date.toLocaleTimeString("en-US")}
        </td>
        <td>
          <Link
            className="text-decoration-none fw-bold ps-2"
            to="#"
            onClick={() => confirmDelete(activity.created_at.S)}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </Link>
        </td>
      </tr>
    );
  });

  let renderResult = (
    <table className="table table-striped table-hover border">
      <thead>
        <tr>
          <th>#</th>
          <th>Activity</th>
          <th>Created At</th>
          <th></th>
        </tr>
      </thead>
      <tbody>{renderActivities}</tbody>
    </table>
  );

  return (
    <div className="p-3 m-1 border rounded bg-white">
      <div className="text-center">
        <h3>Activity</h3>
        <div className="text-end">
          <Link className="text-decoration-none fw-bold" to="/activity/trigger">
            <button className="btn btn-primary me-1">Triggers</button>
          </Link>
        </div>
        <div className="text-start">
          <FontAwesomeIcon icon={faFilter} />
          <select
            onChange={(e) => filterActivities(e.target.value)}
            name="activity_filter"
            className="ms-2 w-25 px-3 py-2 rounded border"
          >
            <option value="">-</option>
            {activityCategories?.map(
              (activityCategory: ActivityCategory) =>
                activityCategory?.category_name?.S && (
                  <option value={activityCategory.category_name.S}>{activityCategory.category_name.S}</option>
                )
            )}
          </select>
        </div>
      </div>
      <div className="text-center my-3">{renderResult}</div>
    </div>
  );
}
